import { ClientWebsockEvent, ServerWebsockEvent } from "../websocketEventTypes";

const IDENTIFY_MESSAGE: ClientWebsockEvent = { eventType: "identify", payload: {} };
const PING_MESSAGE: ClientWebsockEvent = { eventType: "ping", payload: {} };

// Exhaustiveness check — a new ServerWebsockEvent case without a handler below is a type error.
// Unrecognized events still reach here at runtime and are ignored.
const assertServerEventHandled = (_event: never): void => {};

export type WebsocketSessionCallbacks = {
  // Return false to report the send failed (e.g. socket not open). true/void means
  // presumed sent. If false is returned, onRecycleConnection will be called immediately
  // afterwards.
  sendJsonMessage: (message: ClientWebsockEvent) => boolean | void;
  // Fired when the connection is presumed dead (unanswered ping, or a failed send). Caller
  // decides what "recycle" means, e.g. closing the socket so its reconnect logic takes over.
  onRecycleConnection: () => void;
  // The connectionId from the server's "identity" reply, or undefined once the session stops.
  // Feeds platform-specific plumbing (leader-tab ttl refresh on web, auth context on mobile).
  onConnectionId: (connectionId: string | undefined) => void;
  debugLog?: (...args: unknown[]) => void;
};

export type WebsocketSession = {
  // Identifies immediately, then pings every pingIntervalMs until stop().
  start: () => void;
  stop: () => void;
  handleMessage: (message: ServerWebsockEvent) => void;
};

// Owns the identify/ping/pong liveness protocol. Identifies immediately on open (the server
// can't push during $connect, so this is the earliest point it can hand back a connectionId) —
// that round-trip doubles as the first ping. If a ping or identify goes unanswered before the
// next is due, onRecycleConnection fires so the caller can tear down and reconnect.
export const createWebsocketSession = (
  pingIntervalMs: number,
  callbacks: WebsocketSessionCallbacks,
): WebsocketSession => {
  const debugLog = callbacks.debugLog ?? (() => {});
  let awaitingResponse = false;
  let intervalId: ReturnType<typeof setInterval> | undefined;

  const send = (message: ClientWebsockEvent): void => {
    debugLog("WS outgoing:", message.eventType, JSON.stringify(message));
    awaitingResponse = true;
    if (callbacks.sendJsonMessage(message) === false) {
      debugLog("WS send failed; recycling connection");
      callbacks.onRecycleConnection();
    }
  };

  const start = (): void => {
    send(IDENTIFY_MESSAGE);
    intervalId = setInterval(() => {
      if (awaitingResponse) {
        debugLog("WS identity/pong not received since last request; recycling connection");
        callbacks.onRecycleConnection();
        return;
      }
      send(PING_MESSAGE);
    }, pingIntervalMs);
  };

  const stop = (): void => {
    if (intervalId !== undefined) {
      clearInterval(intervalId);
      intervalId = undefined;
    }
    callbacks.onConnectionId(undefined);
  };

  const handleMessage = (message: ServerWebsockEvent): void => {
    debugLog("WS incoming:", message.eventType, JSON.stringify(message));
    const eventType = message.eventType;
    switch (eventType) {
      case "identity": {
        awaitingResponse = false;
        callbacks.onConnectionId(message.payload.connectionId);
        break;
      }
      case "pong": {
        awaitingResponse = false;
        break;
      }
      default: {
        assertServerEventHandled(eventType);
        break;
      }
    }
  };

  return { start, stop, handleMessage };
};
